import { Download } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useMarketplaceConnections } from "@/api/use-marketplace-connections";
import { Button } from "@/shared/ui/button";
import { Select } from "@/shared/ui/select";

import { MARKETPLACE_LABEL } from "../lib/marketplace-labels";
import type { Lifecycle, Marketplace } from "../lib/types";

// ── Status lifecycle codes ───────────────────────────────────────────────────
const STATUS_CODES: Lifecycle[] = [
  "received",
  "reserved",
  "picked",
  "packed",
  "shipped",
  "delivered",
  "cancelled",
  "returned",
  "superseded",
];

// ── Column definitions ───────────────────────────────────────────────────────
interface ColumnDef {
  key: string;
  defaultChecked: boolean;
}

const COLUMNS: ColumnDef[] = [
  { key: "th_order", defaultChecked: true },
  { key: "th_marketplace", defaultChecked: true },
  { key: "th_created", defaultChecked: true },
  { key: "th_customer", defaultChecked: false },
  { key: "th_total", defaultChecked: true },
  { key: "th_currency", defaultChecked: true },
  { key: "th_status", defaultChecked: true },
  { key: "th_payment", defaultChecked: false },
  { key: "oi_seller", defaultChecked: false },
  { key: "oi_seller_name", defaultChecked: false },
  { key: "lbl_tracking", defaultChecked: false },
];

// ── Props ────────────────────────────────────────────────────────────────────
/** The user's export choices, handed to the page to fetch + build the CSV. */
export interface ExportSelection {
  dateFrom: string;
  dateTo: string;
  marketplace: string;
  status: string;
  seller: string;
  /** Selected column keys, in canonical column order. */
  columns: string[];
}

interface ExportConfigProps {
  onGenerate: (selection: ExportSelection) => void;
  isGenerating?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────
export function ExportConfig({ onGenerate, isGenerating = false }: ExportConfigProps): JSX.Element {
  const { t } = useTranslation("orders");
  const { data: connections = [] } = useMarketplaceConnections();
  // Every shop with an id, whatever its status — see OrderFilters for the same rule.
  const sellers = connections.filter((c) => !!c.shop_id);

  // Empty by default: the orders list has no date field yet, so a pre-filled range
  // would silently exclude everything. The user opts into date filtering explicitly.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [marketplace, setMarketplace] = useState<"" | Marketplace>("");
  const [status, setStatus] = useState<"" | Lifecycle>("");
  const [seller, setSeller] = useState("");
  const [columns, setColumns] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultChecked])),
  );

  function toggleColumn(key: string): void {
    setColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleGenerate(): void {
    onGenerate({
      dateFrom,
      dateTo,
      marketplace,
      status,
      seller,
      // Preserve canonical column order regardless of toggle order.
      columns: COLUMNS.filter((c) => columns[c.key]).map((c) => c.key),
    });
  }

  return (
    <div className="rounded-xl border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--sh-1)]">
      {/* Card header */}
      <div className="border-b border-[var(--border-default)] px-5 py-4">
        <h3 className="text-base font-semibold text-[var(--text-primary)]">{t("oe_config")}</h3>
      </div>

      {/* Card body */}
      <div className="flex flex-col gap-5 px-5 py-5">
        {/* Date range */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
            {t("oe_range")}
          </label>
          <div className="flex gap-2.5">
            <input
              type="date"
              aria-label={t("oe_date_from")}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 flex-1 appearance-none rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
            />
            <input
              type="date"
              aria-label={t("oe_date_to")}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 flex-1 appearance-none rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
            />
          </div>
        </div>

        {/* Marketplace + Status selects */}
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1.5">
            <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
              {t("th_marketplace")}
            </label>
            <Select
              className="w-full"
              value={marketplace}
              onChange={(e) => setMarketplace(e.target.value as "" | Marketplace)}
            >
              <option value="">{t("f_all_mkp")}</option>
              {(Object.keys(MARKETPLACE_LABEL) as Marketplace[]).map((mkp) => (
                <option key={mkp} value={mkp}>
                  {MARKETPLACE_LABEL[mkp]}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-1 flex-col gap-1.5">
            <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
              {t("th_status")}
            </label>
            <Select
              className="w-full"
              value={status}
              onChange={(e) => setStatus(e.target.value as "" | Lifecycle)}
            >
              <option value="">{t("f_all_status")}</option>
              {STATUS_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`st_${code}`)}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Seller */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
            {t("th_seller")}
          </label>
          <Select className="w-full" value={seller} onChange={(e) => setSeller(e.target.value)}>
            <option value="">{t("f_all_seller")}</option>
            {sellers.map((c) => (
              <option key={`${c.marketplace}-${c.shop_id}`} value={c.shop_id}>
                {t("seller_option", { name: c.shop_name || c.shop_id, id: c.shop_id })}
              </option>
            ))}
          </Select>
        </div>

        {/* Column checkboxes */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
            {t("oe_columns")}
          </label>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {COLUMNS.map((col) => (
              <label
                key={col.key}
                className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text-primary)]"
              >
                <input
                  type="checkbox"
                  checked={columns[col.key] ?? false}
                  onChange={() => toggleColumn(col.key)}
                  className="h-4 w-4 rounded border-[var(--border-default)] accent-[var(--brand-500)]"
                />
                {t(col.key)}
              </label>
            ))}
          </div>
        </div>

        {/* Generate export — the ONE primary button in this view */}
        <Button
          variant="default"
          onClick={handleGenerate}
          disabled={isGenerating}
          className="mt-1 w-full gap-2"
        >
          <Download className="h-4 w-4" aria-hidden />
          {isGenerating ? "…" : t("oe_generate")}
        </Button>
      </div>
    </div>
  );
}
